import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { faqSummary, cityRealtyPlan, faqData } from '../data/chcaFaqData';
import './ChcaFaq.css';

const SLIDE_TITLES = {
  2: "Chestnut Hill Commercial Area Study Charge",
  3: "Chestnut Hill Commercial Overlay District",
  4: "The Process by the Numbers",
  5: "Comprehensive Study Workstreams",
  6: "Study Recommendations",
  7: "Overlay Zoning: Subdistricts",
  8: "Overlay Zoning: Maximum Heights",
  9: "Overlay Zoning Requirements",
  10: "City Realty Parallel Track",
  11: "City Realty Project",
  12: "City Realty Fiscal Impact",
  13: "City Realty Commercial Percentages",
  14: "Warrant Article 2: City Realty Agreements",
  15: "City Realty’s Proposed Project Timeline",
  16: "City Realty Alternatives",
  17: "City Realty Renderings",
};

const SLIDE_HTML = {
  3: (
    <div className="slide-html-content slide-map-with-legend">
      <img src="/images/chca-slide-03-map.jpg" alt="Overlay district map" className="map-img" />
      <table className="carousel-legend-table">
        <tbody>
          {[
            { color: "#fcd055", label: "City Realty" },
            { color: "#f19099", label: "Growth Companies" },
            { color: "#500710", label: "Chestnut Hill Property Co LLC" },
            { color: "#89ff87", label: "WS Development" },
            { color: "#048300", label: "Salvatore Capital Partners" },
            { color: "#b1b1cd", label: "Chestnut Hill Realty Ventures (40B)" },
            { color: "#63398d", label: "Chestnut Hill Realty Investments (40B)" },
            { color: "#cccccc", label: "Owned by others" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  7: (
    <div className="slide-html-content slide-map-with-legend">
      <img src="/images/chca-slide-07-map.jpg" alt="Subdistricts map" className="map-img" />
      <table className="carousel-legend-table">
        <tbody>
          {[
            { color: "#fddf5d", label: "Chestnut Hill Commercial (CHC-4.0) West" },
            { color: "#ffb600", label: "Chestnut Hill Commercial (CHC-4.0) East" },
            { color: "#ff4546", label: "Chestnut Hill Commercial (CHC-3.0)" },
            { color: "#e800ea", label: "Neighborhood Mixed Use (NMU)" },
            { color: "#e4d3c1", label: "Transit Oriented Mixed Use (TOM)" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  8: (
    <div className="slide-html-content slide-map-with-legend">
      <img src="/images/chca-slide-08-map.jpg" alt="Maximum heights map" className="map-img" />
      <table className="carousel-legend-table">
        <tbody>
          {[
            { color: "#cd3333", label: "125' maximum, 150-175' height bonus" },
            { color: "#dc878a", label: "88' maximum" },
            { color: "#dcaaa9", label: "75' maximum" },
            { color: "#eecccb", label: "50' maximum, 75' height bonus" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  2: (
    <div className="slide-html-content slide-study-charge">
      <p className="study-charge-lead"><strong>Staff-led by Economic Development &amp; Long-Term Planning Division</strong><br />
      Advised By Economic Development Advisory Board (EDAB) and a Community Advisory Group</p>
      <ul className="study-charge-bullets">
        <li>Examine opportunities to incentivize significant net new commercial tax growth</li>
        <li>Enhance livability and identity of the Chestnut Hill commercial area</li>
        <li>Produce recommendations for rezoning proposals and design guidelines</li>
      </ul>
      <p className="study-charge-footer"><em>Authorized by Select Board: November 14, 2023</em></p>
    </div>
  ),
  5: (
    <div className="slide-html-content slide-workstreams">
      {[
        { icon: "/images/chca-icon-market.png", title: "Market Analysis & Existing Conditions", desc: "Commercial market assessment, existing zoning review, land use analysis, infrastructure capacity" },
        { icon: "/images/chca-icon-fiscal.png", title: "Financial Feasibility & Fiscal Impact", desc: "RKG massing test fits, pro forma analysis, fiscal impact modeling, net new tax revenue projections" },
        { icon: "/images/chca-icon-zoning.png", title: "Zoning & Design Guidelines", desc: "Overlay district zoning framework, design guidelines & standards, massing controls development" },
        { icon: "/images/chca-icon-transport.png", title: "Transportation & Multimodal Safety", desc: "Existing conditions analysis, multimodal capacity improvements, bike & pedestrian network plan" },
        { icon: "/images/chca-icon-community.png", title: "Community Engagement", desc: "In-person and virtual workshops, neighborhood meetings, study area site walks, public comment" },
        { icon: "/images/chca-icon-housing.png", title: "Housing & Inclusionary Zoning", desc: "Affordable housing proposals, inclusionary requirements, Housing Advisory Board review" },
      ].map((item, i) => (
        <div key={i} className="workstream-item">
          <img src={item.icon} alt="" className="workstream-icon-img" />
          <div>
            <span className="workstream-title">{item.title}</span><br />
            <span className="workstream-desc">{item.desc}</span>
          </div>
        </div>
      ))}
    </div>
  ),
  6: (
    <div className="slide-html-content slide-recommendations">
      {[
        { color: "#1a6b7a", title: "Overlay District Zoning", desc: "Incentivize high-impact tax generating commercial uses in a walkable, mixed-use neighborhood" },
        { color: "#1a6b7a", title: "Design Guidelines & Standards", desc: "Promote a cohesive district-wide commercial area character and aesthetic" },
        { color: "#1a6b7a", title: "Transportation Improvements", desc: "Improve multimodal safety, plan bike & pedestrian network, and analyze infrastructure capacity" },
        { color: "#d4a843", title: "Commercial Vibrancy Strategies", desc: "Enhance the livability, identity, and economic vitality of the Chestnut Hill commercial area" },
      ].map((item, i) => (
        <div key={i} className="recommendation-item">
          <div className="recommendation-bar" style={{ background: item.color }} />
          <div>
            <span className="recommendation-title">{item.title}</span><br />
            <span className="recommendation-desc">{item.desc}</span>
          </div>
        </div>
      ))}
    </div>
  ),
  9: (
    <div className="slide-html-content slide-key-sections">
      <div className="key-sections-bar" />
      <div className="key-sections-list">
        {[
          "Minimum Commercial Percentage",
          "Minimum Ground Floor Commercial Percentage",
          "Allowed Uses",
          "Special Permits and Design Reviews",
          "Maximum Heights, Maximum Floor Area Ratios, Minimum Setbacks and Stepbacks",
          "Open Space (including Large Site Open Space and Tree Canopy)",
          "Vehicle and Bicycle Parking",
          "Vehicle and Pedestrian Circulation",
          "Infrastructure Improvements",
        ].map((item, i) => (
          <div key={i} className="key-section-item">
            <span className="key-section-icon" />
            <span className="key-section-text">{item}</span>
          </div>
        ))}
      </div>
    </div>
  ),
  10: (
    <div className="slide-html-content slide-timeline">
      {[
        { date: "May 13, 2024", text: "City Realty bought Chestnut Hill Office Park", sub: null },
        { date: <span>Sep 2024 –<br />Sep 2025</span>, text: "City Realty presented 4 designs to the Community Advisory Group", sub: "September 2024, October 2024, January 2025, September 2025" },
        { date: "Sep 2025 – Dec 2025", text: "The Town negotiated with City Realty to align their September 2025 program with Town zoning and neighborhood goals", sub: null },
        { date: <span>Oct 2025 –<br />Feb 2026</span>, text: "The Town negotiated with City Realty for a Memorandum of Agreement and a Tax Certainty Agreement", sub: null },
      ].map((item, i) => (
        <div key={i} className="timeline-item">
          <div className="timeline-bar" />
          <div className="timeline-date">{item.date}</div>
          <div className="timeline-text">
            <strong>{item.text}</strong>
            {item.sub && <><br /><span className="timeline-sub">{item.sub}</span></>}
          </div>
        </div>
      ))}
    </div>
  ),
  12: (
    <div className="slide-html-content slide-mobile-fiscal">
      <p className="mobile-fiscal-subtitle"><em>Based on RKG Associates fiscal model (all values are $/year)</em></p>
      <table className="mobile-fiscal-table">
        <tbody>
          <tr>
            <td>Residential Real Property Taxes</td>
            <td className="mobile-fiscal-range">$3.0M – $3.3M</td>
          </tr>
          <tr>
            <td>Commercial Real &amp; Personal Property Taxes</td>
            <td className="mobile-fiscal-range">$2.9M – $3.8M</td>
          </tr>
          <tr>
            <td>Local Rooms Tax</td>
            <td className="mobile-fiscal-range">$0.8M – $1.3M</td>
          </tr>
          <tr>
            <td>Motor Vehicle Taxes</td>
            <td className="mobile-fiscal-range">$0.1M – $0.1M</td>
          </tr>
          <tr className="mobile-fiscal-summary-row">
            <td>REVENUE</td>
            <td className="mobile-fiscal-range">$6.7M – $8.6M</td>
          </tr>
          <tr className="mobile-fiscal-after-summary">
            <td>Cost of Services</td>
            <td className="mobile-fiscal-range">($1.4M) – ($1.2M)</td>
          </tr>
          <tr className="mobile-fiscal-summary-row">
            <td>FISCAL IMPACT</td>
            <td className="mobile-fiscal-range">$5.3M – $7.4M</td>
          </tr>
          <tr className="mobile-fiscal-after-summary">
            <td>Pre-development Property Taxes</td>
            <td className="mobile-fiscal-range">($1.1M) – ($1.1M)</td>
          </tr>
          <tr className="mobile-fiscal-total-row">
            <td>NET NEW TAX GROWTH</td>
            <td className="mobile-fiscal-range">$4.2M – $6.3M</td>
          </tr>
        </tbody>
      </table>
    </div>
  ),
  13: (
    <div className="slide-html-content slide-commercial-pct">
      <p className="commercial-pct-purpose">
        Minimum commercial percentage requirements and incentives for certain uses work together to maximize commercial development.
      </p>
      <table className="commercial-pct-table">
        <thead>
          <tr>
            <th></th>
            <th>Gross Floor Area Only</th>
            <th>Effective Gross Floor Area With Incentives</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="row-label">Project</td>
            <td>46.2%</td>
            <td className="bold-cell">63.4%</td>
          </tr>
          <tr>
            <td className="row-label">Phase 1 (Building A)</td>
            <td>42.5%</td>
            <td className="bold-cell">62.7%</td>
          </tr>
          <tr>
            <td className="row-label">Phase 2 (Buildings B &amp; C)</td>
            <td>51.6%</td>
            <td className="bold-cell">64.5%</td>
          </tr>
        </tbody>
      </table>
    </div>
  ),
};

const SLIDE_MOBILE_HTML = {
  4: (
    <div className="slide-html-content slide-mobile-numbers">
      <div className="mobile-numbers-subtitle">Spring 2024 – Spring 2026</div>
      <div className="mobile-numbers-grid">
        {[
          { num: "21", label: "CAG Meetings" },
          { num: "6", label: "Select Board Updates" },
          { num: "37+", label: "Total Public Meetings" },
          { num: "11", label: "CAG Members" },
        ].map((item, i) => (
          <div key={i} className="mobile-numbers-card">
            <div className="mobile-numbers-value">{item.num}</div>
            <div className="mobile-numbers-label">{item.label}</div>
          </div>
        ))}
      </div>
      <div className="mobile-numbers-footer"><em>The study encompassed far more area than any single development proposal.</em></div>
    </div>
  ),
  3: (
    <div className="slide-html-content slide-mobile-map">
      <img src="/images/chca-slide-03-map.jpg" alt="Overlay district map" className="mobile-map-img" />
      <table className="mobile-legend-table">
        <tbody>
          {[
            { color: "#fcd055", label: "City Realty" },
            { color: "#f19099", label: "Growth Companies" },
            { color: "#500710", label: "Chestnut Hill Property Co LLC" },
            { color: "#89ff87", label: "WS Development" },
            { color: "#048300", label: "Salvatore Capital Partners" },
            { color: "#b1b1cd", label: "Chestnut Hill Realty Ventures (40B)" },
            { color: "#63398d", label: "Chestnut Hill Realty Investments (40B)" },
            { color: "#cccccc", label: "Owned by others" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  7: (
    <div className="slide-html-content slide-mobile-map">
      <img src="/images/chca-slide-07-map.jpg" alt="Subdistricts map" className="mobile-map-img" />
      <table className="mobile-legend-table">
        <tbody>
          {[
            { color: "#fddf5d", label: "Chestnut Hill Commercial (CHC-4.0) West" },
            { color: "#ffb600", label: "Chestnut Hill Commercial (CHC-4.0) East" },
            { color: "#ff4546", label: "Chestnut Hill Commercial (CHC-3.0)" },
            { color: "#e800ea", label: "Neighborhood Mixed Use (NMU)" },
            { color: "#e4d3c1", label: "Transit Oriented Mixed Use (TOM)" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  8: (
    <div className="slide-html-content slide-mobile-map">
      <img src="/images/chca-slide-08-map.jpg" alt="Maximum heights map" className="mobile-map-img" />
      <table className="mobile-legend-table">
        <tbody>
          {[
            { color: "#cd3333", label: "125' maximum, 150-175' height bonus" },
            { color: "#dc878a", label: "88' maximum" },
            { color: "#dcaaa9", label: "75' maximum" },
            { color: "#eecccb", label: "50' maximum, 75' height bonus" },
          ].map((item, i) => (
            <tr key={i}>
              <td><span className="legend-color-block" style={{ background: item.color }} /></td>
              <td className="legend-label">{item.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  10: (
    <div className="slide-html-content slide-mobile-parallel">
      {[
        { date: "May 13, 2024", text: "City Realty bought Chestnut Hill Office Park", sub: null },
        { date: "Sep 2024 – Sep 2025", text: "City Realty presented 4 designs to the Community Advisory Group", sub: "September 2024, October 2024, January 2025, September 2025" },
        { date: "Sep 2025 – Dec 2025", text: "The Town negotiated with City Realty to align their September 2025 program with Town zoning and neighborhood goals", sub: null },
        { date: "Oct 2025 – Feb 2026", text: "The Town negotiated with City Realty for a Memorandum of Agreement and a Tax Certainty Agreement", sub: null },
      ].map((item, i) => (
        <div key={i} className="mobile-parallel-row">
          <div className="mobile-parallel-bar" />
          <div className="mobile-parallel-content">
            <div className="mobile-parallel-date">{item.date}</div>
            <div className="mobile-parallel-text">
              <strong>{item.text}</strong>
              {item.sub && <><br /><span className="mobile-parallel-sub">{item.sub}</span></>}
            </div>
          </div>
        </div>
      ))}
    </div>
  ),
  12: (
    <div className="slide-html-content slide-mobile-fiscal">
      <p className="mobile-fiscal-subtitle"><em>Based on RKG Associates fiscal model (all values are $/year)</em></p>
      <table className="mobile-fiscal-table">
        <tbody>
          <tr>
            <td>Residential Real Property Taxes</td>
            <td className="mobile-fiscal-range">$3.0M – $3.3M</td>
          </tr>
          <tr>
            <td>Commercial Real &amp; Personal Property Taxes</td>
            <td className="mobile-fiscal-range">$2.9M – $3.8M</td>
          </tr>
          <tr>
            <td>Local Rooms Tax</td>
            <td className="mobile-fiscal-range">$0.8M – $1.3M</td>
          </tr>
          <tr>
            <td>Motor Vehicle Taxes</td>
            <td className="mobile-fiscal-range">$0.1M – $0.1M</td>
          </tr>
          <tr className="mobile-fiscal-summary-row">
            <td>REVENUE</td>
            <td className="mobile-fiscal-range">$6.7M – $8.6M</td>
          </tr>
          <tr className="mobile-fiscal-after-summary">
            <td>Cost of Services</td>
            <td className="mobile-fiscal-range">($1.4M) – ($1.2M)</td>
          </tr>
          <tr className="mobile-fiscal-summary-row">
            <td>FISCAL IMPACT</td>
            <td className="mobile-fiscal-range">$5.3M – $7.4M</td>
          </tr>
          <tr className="mobile-fiscal-after-summary">
            <td>Pre-development Property Taxes</td>
            <td className="mobile-fiscal-range">($1.1M) – ($1.1M)</td>
          </tr>
          <tr className="mobile-fiscal-total-row">
            <td>NET NEW TAX GROWTH</td>
            <td className="mobile-fiscal-range">$4.2M – $6.3M</td>
          </tr>
        </tbody>
      </table>
    </div>
  ),
  14: (
    <div className="slide-html-content slide-mobile-agreements">
      <div className="mobile-agreement-section">
        <div className="mobile-agreement-heading">Memorandum of Agreement</div>
        <ul className="mobile-agreement-list">
          <li>Lays out project as 3 buildings: 14, 12 and 7 stories</li>
          <li>Pays $10.93 million Affordable Housing Trust Fund</li>
          <li>Provides ~$12 million Transportation and Public Safety Infrastructure Mitigation</li>
          <li>Heath Street Protections</li>
          <li>Neighborhood Enhancing Design</li>
          <li>Neighborhood Advisory Committee</li>
          <li>Retail Support Fund</li>
          <li>Sustainability Commitments and Explorations</li>
          <li>And More</li>
        </ul>
      </div>
      <div className="mobile-agreement-section">
        <div className="mobile-agreement-heading">Tax Certainty Agreement</div>
        <ul className="mobile-agreement-list">
          <li>Protects Property Tax Revenue in case a building is bought by a tax-exempt organization</li>
          <li>Protects Hotel Excise Tax Revenue in case the hotel is converted to a different use</li>
        </ul>
      </div>
      <div className="mobile-agreement-footer">Both Agreements survive transfer of ownership.</div>
    </div>
  ),
  15: (
    <div className="slide-html-content slide-mobile-timeline">
      {[
        { time: "May 23, 2026", event: "★ Town Meeting" },
        { time: "May – Jul 2026", event: "City Realty Prepares Site Plan Review" },
        { time: "Jul – Aug 2026", event: "Design Advisory Team Recommendation to Planning Board" },
        { time: "Sep 2026", event: "Planning Board Site Plan Approval" },
        { time: "Sep – Dec 2026", event: "City Realty Creates Transportation Demand Management Plan" },
        { time: "Oct – Nov 2026", event: "Zoning Board of Appeals Approves Special Permit" },
        { time: "Dec 2026 – Nov 2027", event: "City Realty Creates Phase 1 Construction Drawings" },
        { time: "Jan 2027", event: "Zoning Board Decision Appeal Period" },
        { time: "Jul – Dec 2027", event: "Building Department Issues Phase 1 Building Permit" },
      ].map((item, i) => (
        <div key={i} className="mobile-timeline-row">
          <div className="mobile-timeline-time">{item.time}</div>
          <div className="mobile-timeline-event">{item.event}</div>
        </div>
      ))}
      <div className="mobile-timeline-note"><strong>Construction will not begin before Spring 2028.</strong></div>
      <div className="mobile-timeline-footer">All dates are estimates and subject to change</div>
    </div>
  ),
  16: (
    <div className="slide-html-content slide-mobile-alternatives">
      <div className="mobile-alt-headline">If the Warrant Articles don't pass Town Meeting, City Realty could:</div>
      {[
        { outcome: "Re-lease the Citizens Bank building", impact: "Town maintains existing level of tax revenue" },
        { outcome: "Wait and develop their proposed housing project under Chapter 40B", impact: "The land remains as it is until 40B window opens, after which something akin to the 40B design proposed October 2025 (200 housing units) moves through the 40B process. No new commercial tax revenue." },
        { outcome: "File a Housing Choice warrant article and develop housing only", impact: "Town Meeting votes on a housing choice warrant article that allows economically viable housing development. No new commercial tax revenue." },
      ].map((item, i) => (
        <div key={i} className="mobile-alt-row">
          <div className="mobile-alt-outcome">{item.outcome}</div>
          <div className="mobile-alt-impact">{item.impact}</div>
        </div>
      ))}
    </div>
  ),
  17: (
    <div className="slide-html-content slide-mobile-renderings">
      <div className="mobile-renderings-subtitle"><em>These preliminary designs will change significantly during Design Review.</em></div>
    </div>
  ),
};

const SLIDE_CAPTIONS = {
  18: "Bird\u2019s eye view of the full campus, facing northeast",
  19: "Interior roadway next to Boylston Street, facing southeast: Ground-level retail and plaza",
  20: "Central plaza from Heath Street, facing north: Trees, retail, and gathering space",
  21: "Boylston Street, facing southwest: Step backs and varied massing",
  22: "Boylston Street, facing southeast: Full campus view with varied building forms",
  23: "Heath Street, facing northwest: Landscaped setback and step backs above",
};

const SLIDE_COUNT = 22;
const SLIDES = Array.from({ length: SLIDE_COUNT }, (_, i) => {
  const slideNum = i + 2;
  const num = String(slideNum).padStart(2, '0');
  return {
    src: `/images/chca-slide-${num}.jpg`,
    title: SLIDE_TITLES[slideNum] || null,
    html: SLIDE_HTML[slideNum] || null,
    mobileHtml: SLIDE_MOBILE_HTML[slideNum] || null,
    caption: SLIDE_CAPTIONS[slideNum] || null,
  };
});

function ChcaFaq() {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse tab and slide from URL path
  const getTabFromPath = useCallback(() => {
    const path = location.pathname.replace(/^\/chca-faq\/?/, '');
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'slides') return { tab: 'overview', slide: parts[1] ? parseInt(parts[1], 10) - 1 : null };
    if (parts[0] === 'faq') return { tab: 'faq', slide: null };
    if (parts[0] === 'overview') return { tab: 'info', slide: null };
    return { tab: 'info', slide: null };
  }, [location.pathname]);

  const { tab: urlTab, slide: urlSlide } = getTabFromPath();

  const [activeTab, setActiveTabState] = useState(urlTab);
  const [currentSlide, setCurrentSlide] = useState(urlSlide != null && urlSlide >= 0 && urlSlide < SLIDE_COUNT ? urlSlide : 0);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const invalidLinkRef = useRef(null);

  // Update state when URL changes (e.g. back/forward navigation)
  useEffect(() => {
    const { tab, slide } = getTabFromPath();
    setActiveTabState(tab);
    if (tab === 'overview' && slide != null && slide >= 0 && slide < SLIDE_COUNT) {
      setCurrentSlide(slide);
    }
  }, [location.pathname, getTabFromPath]);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    if (tab === 'info') navigate('/chca-faq/overview', { replace: false });
    else if (tab === 'overview') navigate('/chca-faq/slides', { replace: false });
    else if (tab === 'faq') navigate('/chca-faq/faq', { replace: false });
  }, [navigate]);

  useEffect(() => {
    document.title = 'CHCA — Chestnut Hill Commercial Area';
  }, []);

  // Handle hash-based deep linking to specific questions
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    setActiveTabState('faq');
    navigate('/chca-faq/faq' + location.hash, { replace: true });

    const validIds = faqData.map(item => item.id);
    if (validIds.includes(hash)) {
      setExpandedItems(prev => new Set(prev).add(hash));
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setInvalidLink(true);
      setTimeout(() => {
        if (invalidLinkRef.current) {
          invalidLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, navigate]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    navigate(`/chca-faq/slides/${index + 1}`, { replace: true });
  }, [navigate]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => {
      const next = (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT;
      navigate(`/chca-faq/slides/${next + 1}`, { replace: true });
      return next;
    });
  }, [navigate]);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => {
      const next = (prev + 1) % SLIDE_COUNT;
      navigate(`/chca-faq/slides/${next + 1}`, { replace: true });
      return next;
    });
  }, [navigate]);

  const toggleItem = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAllItems = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(faqData.map(item => item.id)));
    }
    setExpandAll(!expandAll);
  };

  const formatText = (text) => {
    let formatted = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    const paragraphs = formatted.split('\n\n');

    // Group consecutive numbered-item paragraphs together
    const grouped = [];
    let i = 0;
    while (i < paragraphs.length) {
      const p = paragraphs[i];
      const lines = p.split('\n');
      const isNumbered = lines.some(l => /^\d+\.\s/.test(l.trim()));
      if (isNumbered) {
        // Collect consecutive numbered paragraphs
        const introLines = [];
        const numberedItems = [];
        // Check if this paragraph has intro text before the first numbered item
        const firstNumIdx = lines.findIndex(l => /^\d+\.\s/.test(l.trim()));
        if (firstNumIdx > 0) {
          introLines.push(...lines.slice(0, firstNumIdx).filter(l => l.trim()));
        }
        // Collect numbered items from this and subsequent paragraphs
        const numLines = lines.slice(firstNumIdx).filter(l => l.trim());
        numberedItems.push(numLines.join(' '));
        i++;
        while (i < paragraphs.length) {
          const nextLines = paragraphs[i].split('\n').filter(l => l.trim());
          if (nextLines.length > 0 && /^\d+\.\s/.test(nextLines[0].trim())) {
            numberedItems.push(nextLines.join(' '));
            i++;
          } else {
            break;
          }
        }
        grouped.push({ type: 'ol', introLines, numberedItems });
      } else if (p.includes('\u2022')) {
        grouped.push({ type: 'ul', paragraph: p });
        i++;
      } else {
        grouped.push({ type: 'p', paragraph: p });
        i++;
      }
    }

    return grouped.map((item, idx) => {
      if (item.type === 'ul') {
        const lines = item.paragraph.split('\n');
        const firstBulletIndex = lines.findIndex(l => l.trim().startsWith('\u2022'));
        const introLines = lines.slice(0, firstBulletIndex).filter(l => l.trim());
        const bullets = lines.slice(firstBulletIndex).filter(l => l.trim().startsWith('\u2022'));
        return (
          <div key={idx} className="answer-section">
            {introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: introLines.join('<br/>') }} />
            )}
            <ul>
              {bullets.map((bullet, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: bullet.replace(/^\u2022\s*/, '').trim() }} />
              ))}
            </ul>
          </div>
        );
      }
      if (item.type === 'ol') {
        return (
          <div key={idx} className="answer-section">
            {item.introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: item.introLines.join('<br/>') }} />
            )}
            <ol>
              {item.numberedItems.map((ni, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: ni.replace(/^\d+\.\s*/, '').trim() }} />
              ))}
            </ol>
          </div>
        );
      }
      return <p key={idx} dangerouslySetInnerHTML={{ __html: item.paragraph.replace(/\n/g, '<br/>') }} />;
    });
  };

  const formatAnswer = formatText;

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="chca-faq-page">
      <a href="#main-content" className="chca-skip-link">Skip to main content</a>
      <Header
        title="Chestnut Hill Commercial Area"
      >
        <div className="chca-header-tabs">
          <button
            className={`chca-header-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            aria-pressed={activeTab === 'info'}
          >
            Overview
          </button>
          <button
            className={`chca-header-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            aria-pressed={activeTab === 'overview'}
          >
            Slides
          </button>
          <button
            className={`chca-header-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
            aria-pressed={activeTab === 'faq'}
          >
            FAQ
          </button>
        </div>
      </Header>


      <main id="main-content" tabIndex={-1} className="faq-content">

        {/* ===== INFO (OVERVIEW) PANEL ===== */}
        {activeTab === 'info' && (
          <div className="faq-panel">
            {/* Summary Section */}
            <section className="summary-section">
              <div className="version-badge">{faqSummary.version} - {faqSummary.date}</div>
              <div className="summary-text">
                {formatText(faqSummary.intro)}
              </div>
            </section>

            {/* City Realty Plan Highlights */}
            <section className="highlight-section">
              <h2>City Realty's Proposed Development</h2>
              <div className="highlight-text">
                {formatText(cityRealtyPlan.description)}
              </div>
              <h3>{cityRealtyPlan.benefitsIntro || 'Key Benefits from Agreement'}</h3>
              <ul className="benefits-list">
                {cityRealtyPlan.benefits.map((benefit, i) => (
                  <li key={i}>{benefit}</li>
                ))}
              </ul>
              <p className="contingency-note">{cityRealtyPlan.contingency}</p>
            </section>
          </div>
        )}

        {/* ===== SLIDES PANEL ===== */}
        {activeTab === 'overview' && (
          <div className="overview-panel">
            {/* Carousel for larger screens */}
            <div className="carousel-container">
              <button className="carousel-arrow carousel-prev" onClick={prevSlide} aria-label="Previous slide">&#8249;</button>
              <div className="carousel-viewport">
                {SLIDES[currentSlide].title && (
                  <div className="slide-title-bar">{SLIDES[currentSlide].title}</div>
                )}
                {SLIDES[currentSlide].html ? (
                  SLIDES[currentSlide].html
                ) : (
                  <img
                    src={SLIDES[currentSlide].src}
                    alt={`Slide ${currentSlide + 1} of ${SLIDE_COUNT}`}
                    className="carousel-slide"
                  />
                )}
                {SLIDES[currentSlide].caption && (
                  <div className="slide-caption-bar">
                    <div className="slide-caption-gold-line" />
                    <div className="slide-caption-text">{SLIDES[currentSlide].caption}</div>
                    <div className="slide-caption-bottom-bar" />
                  </div>
                )}
                <div className="carousel-counter">{currentSlide + 1} / {SLIDE_COUNT}</div>
              </div>
              <button className="carousel-arrow carousel-next" onClick={nextSlide} aria-label="Next slide">&#8250;</button>
            </div>

            <div className="carousel-dots">
              {SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  className={`carousel-dot ${idx === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* Stacked slides for small screens */}
            <div className="slides-stacked">
              {SLIDES.map((slide, idx) => (
                <div key={idx} className="stacked-slide-wrapper">
                  {slide.title && (
                    <div className="slide-title-bar">{slide.title}</div>
                  )}
                  {slide.mobileHtml ? (
                    slide.mobileHtml
                  ) : slide.html ? (
                    slide.html
                  ) : (
                    <img
                      src={slide.src}
                      alt={`Slide ${idx + 1} of ${SLIDE_COUNT}`}
                      className="stacked-slide"
                    />
                  )}
                  {slide.caption && (
                    <div className="slide-caption-bar">
                      <div className="slide-caption-gold-line" />
                      <div className="slide-caption-text">{slide.caption}</div>
                      <div className="slide-caption-bottom-bar" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== FAQ PANEL ===== */}
        {activeTab === 'faq' && (
          <div className="faq-panel">
            {/* Invalid link notice */}
            {invalidLink && (
              <div className="invalid-link-notice" role="alert" ref={invalidLinkRef}>
                The link you followed refers to a question that has been removed from this FAQ.
              </div>
            )}

            {/* FAQ Accordion */}
            <section className="faq-section">
              <div className="faq-header">
                <h2>Questions & Answers</h2>
                <button className="expand-toggle" onClick={toggleAllItems} aria-pressed={expandAll}>
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              <div className="faq-list">
                {faqData.map((item) => (
                  <div
                    key={item.id}
                    id={item.id}
                    className={`faq-item ${expandedItems.has(item.id) ? 'expanded' : ''}`}
                  >
                    <h3 className="faq-question-heading"><button
                      className="faq-question"
                      onClick={() => toggleItem(item.id)}
                      aria-expanded={expandedItems.has(item.id)}
                    >
                      <span className="question-text">{item.question}</span>
                      <span className="expand-icon" aria-hidden="true">{expandedItems.has(item.id) ? '\u2212' : '+'}</span>
                    </button></h3>
                    {expandedItems.has(item.id) && (
                      <div className="faq-answer">
                        {formatAnswer(item.answer)}
                        {item.image && (
                          <div className="faq-image">
                            <img src={item.image.src} alt={item.image.alt} />
                          </div>
                        )}
                        <div className="copy-link-wrapper">
                          <button
                            className="copy-link-button"
                            onClick={(e) => { e.stopPropagation(); copyLink(item.id); }}
                            aria-label={copiedId === item.id ? 'Link copied to clipboard' : 'Copy link to this question'}
                          >
                            {copiedId === item.id ? 'Copied!' : '\uD83D\uDD17'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {faqSummary.disclaimer && (
          <DisclaimerFooter>
            <div dangerouslySetInnerHTML={{ __html: faqSummary.disclaimer }} />
          </DisclaimerFooter>
        )}
      </main>
    </div>
  );
}

export default ChcaFaq;
